import { MapPin, Phone, Clock } from "lucide-react";
import { BRAND } from "./data";
import { Reveal } from "./Reveal";
import { useContent } from "./ContentContext";
import { useTx, txtStyle, multiline } from "./siteText";

export const Visit = () => {
  const { settings } = useContent();
  const tx = useTx();
  const phone = settings.phone || BRAND.phone;
  const address = settings.address || BRAND.address;
  const phoneHref = `tel:+1${phone.replace(/\D/g, "")}`;
  const mapsHref = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
  const hours = settings.hours || [];

  return (
    <section id="visit" className="relative bg-cantina-pink text-cantina-cream" data-testid="visit-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal>
            <p className="font-script text-cantina-marigold mb-2" style={txtStyle(tx("visit.eyebrow"))}>{tx("visit.eyebrow").text}</p>
            <h2 className="font-display tracking-tight leading-[1.02] mb-4" style={txtStyle(tx("visit.heading"))}>
              {multiline(tx("visit.heading").text)}
            </h2>
            <div className="dash-rule w-28 mb-12" />
          </Reveal>

          <div className="space-y-8 max-w-xl">
            <Reveal delay={0.1}>
              <div className="flex gap-5">
                <MapPin className="text-cantina-marigold shrink-0 mt-1" size={22} />
                <div>
                  <p className="font-sans tracking-[0.25em] uppercase text-cantina-cream/60 mb-1" style={txtStyle(tx("visit.addressLabel"))}>{tx("visit.addressLabel").text}</p>
                  <a href={mapsHref} target="_blank" rel="noopener noreferrer" className="font-display text-xl md:text-2xl hover:text-cantina-marigold transition-colors" data-testid="visit-address">{address}</a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="flex gap-5">
                <Phone className="text-cantina-marigold shrink-0 mt-1" size={22} />
                <div>
                  <p className="font-sans tracking-[0.25em] uppercase text-cantina-cream/60 mb-1" style={txtStyle(tx("visit.reservationsLabel"))}>{tx("visit.reservationsLabel").text}</p>
                  <a href={phoneHref} className="font-display text-xl md:text-2xl hover:text-cantina-marigold transition-colors" data-testid="visit-phone">{phone}</a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.26}>
              <div className="flex gap-5">
                <Clock className="text-cantina-marigold shrink-0 mt-1" size={22} />
                <div className="w-full">
                  <p className="font-sans tracking-[0.25em] uppercase text-cantina-cream/60 mb-3" style={txtStyle(tx("visit.hoursLabel"))}>{tx("visit.hoursLabel").text}</p>
                  <div className="space-y-2 max-w-sm">
                    {hours.map((h, i) => (
                      <div key={i} className="flex justify-between border-b border-cantina-cream/15 pb-2">
                        <span className="font-sans text-sm font-medium">{h.day}</span>
                        <span className="font-sans text-sm text-cantina-cream/75">{h.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.34}>
              <a href={BRAND.orderUrl} target="_blank" rel="noopener noreferrer" className="btn-primary inline-block bg-cantina-marigold text-cantina-cocoa font-sans tracking-wide px-9 py-4 rounded-full mt-2" style={{ boxShadow: "0 12px 30px rgba(242,182,50,0.35)", ...txtStyle(tx("visit.orderLabel")) }} data-testid="visit-order-button">
                {tx("visit.orderLabel").text}
              </a>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15}>
          <div className="rounded-3xl overflow-hidden border-4 border-cantina-cream/20 shadow-2xl h-[320px] lg:h-[460px]" data-testid="visit-map">
            <iframe
              title="Pink Tequila location map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`}
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
